import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import Dialog from '../../../../atoms/feedback/Dialog'
import { FlexColumn } from '../../../../atoms/layout/Flex'
import FormItem from '../../../../molecules/inputs/FormItem'
import { VisitorRegistrationFormWorkerFields } from '../../../../../types/Company'
import TextField from '../../../../atoms/inputs/TextField'
import RadioControl from '../../../../atoms/inputs/RadioControl'
import DateControl from '../../../../atoms/inputs/DateControl'
import PostalCodeField from '../../../../molecules/inputs/PostalCodeField'
import Button from '../../../../atoms/inputs/Button'
import { CompanyActions } from '../../../../../store/company'
import useValidationRule from '../../../../../hooks/useValidationRule'
import { useVisitorRegistrationFormWorkerEditDialog } from '../../../../../hooks/useDialog'

export default function VisitorRegistrationFormWorkerEditDialog() {
  const { state, close } = useVisitorRegistrationFormWorkerEditDialog()
  const validationRule = useValidationRule()
  const form = useForm<VisitorRegistrationFormWorkerFields>({
    resolver: yupResolver(validationRule.visitorRegistrationFormWorker),
    defaultValues: state?.worker,
  })
  const { errors } = form.formState
  const dispatch = useDispatch()

  const handleUpdate = useCallback(
    (fields: VisitorRegistrationFormWorkerFields) => {
      if (state) {
        dispatch(CompanyActions.updateVisitorRegistrationFormWorker({ workerId: state.worker.id, fields }))
      }
    },
    [dispatch, state]
  )

  return (
    <Dialog
      open={state !== undefined}
      onClose={close}
      title="労働者情報の編集"
      actions={<Button type="submit">更新</Button>}
      onSubmit={form.handleSubmit(handleUpdate)}
    >
      <FlexColumn>
        <FormItem label="氏名" required>
          <TextField register={form.register('name')} error={errors.name?.message} />
        </FormItem>
        <FormItem label="かな" required>
          <TextField register={form.register('kana')} error={errors.kana?.message} />
        </FormItem>
        <FormItem label="性別" required>
          <RadioControl
            items={[
              { value: 1, label: '男性' },
              { value: 2, label: '女性' },
            ]}
            name="gender"
            control={form.control}
            row
            error={errors.gender?.message}
          />
        </FormItem>
        <FormItem label="生年月日" required>
          <DateControl name="birthDate" control={form.control} error={errors.birthDate?.message} />
        </FormItem>
        <FormItem label="郵便番号" required>
          <PostalCodeField
            getValues={form.getValues}
            register={form.register('zipCode')}
            onSearchAddress={(address) => form.setValue('address', address)}
            error={errors.zipCode}
          />
        </FormItem>
        <FormItem label="住所" required>
          <TextField register={form.register('address')} error={errors.address?.message} />
        </FormItem>
        <FormItem label="電話番号" required>
          <TextField register={form.register('phoneNumber')} error={errors.phoneNumber?.message} />
        </FormItem>
        <FormItem label="保有資格">
          <TextField register={form.register('qualification')} error={errors.qualification?.message} />
        </FormItem>
        <FormItem label="血液型" required>
          <RadioControl
            items={[
              { value: 'A', label: 'A型' },
              { value: 'B', label: 'B型' },
              { value: 'O', label: 'O型' },
              { value: 'AB', label: 'AB型' },
            ]}
            name="bloodType"
            control={form.control}
            row
            error={errors.bloodType?.message}
          />
        </FormItem>
        <FormItem label="健康保険" required>
          <RadioControl
            items={[
              { value: '協会けんぽ', label: '協会けんぽ' },
              { value: '健康保険組合', label: '健康保険組合' },
              { value: '共済組合', label: '共済組合' },
              { value: '国民健康保険', label: '国民健康保険' },
              { value: '国民健康保険組合', label: '国民健康保険組合' },
              { value: '後期高齢者医療制度', label: '後期高齢者医療制度' },
            ]}
            name="healthInsuranceName"
            control={form.control}
            row
            error={errors.healthInsuranceName?.message}
          />
          <TextField
            register={form.register('healthInsuranceLastDigits')}
            placeholder="保険番号下4桁"
            error={errors.healthInsuranceLastDigits?.message}
          />
        </FormItem>
        <FormItem label="年金保険" required>
          <RadioControl
            items={[
              { value: '国民年金', label: '国民年金' },
              { value: '厚生年金保険', label: '厚生年金保険' },
              { value: '年金受給者', label: '年金受給者' },
            ]}
            name="pensionInsuranceName"
            control={form.control}
            row
            error={errors.healthInsuranceName?.message}
          />
          <TextField
            register={form.register('pensionInsuranceLastDigits')}
            placeholder="保険番号下4桁"
            error={errors.pensionInsuranceLastDigits?.message}
          />
        </FormItem>
        <FormItem label="雇用保険">
          <TextField
            register={form.register('employmentInsuranceLastDigits')}
            placeholder="保険番号下4桁"
            error={errors.employmentInsuranceLastDigits?.message}
          />
        </FormItem>
      </FlexColumn>
    </Dialog>
  )
}
